/**
 * A service allowing memoization of the function results
 * this should help us to more flexible introduce data dependencies
 * Ex: Service functions C() and B() need the A() service function result to process a specific case.
 * Instead of calculating the result prior to the service calls we can just pass this service as an argument to
 * the both services and access the memoized result with arbitrary and arbitrary count of times being sure
 * the same calculation is not performed twice.
 */
export class ContextService {
	private readonly storage = new Map<(...a: any) => any, Map<string, any>>();

	/**
	 * @param selector the function we want to the memoized, receives this as the current service
	 * @param args call arguments. Prefer primitive types here and small objects
	 * @returns
	 */
	select<TSelector extends (...args: any) => any>(selector: TSelector, ...args: Parameters<TSelector>): ReturnType<TSelector> {
		const argsKey = JSON.stringify(args);
		if (this.storage.has(selector) && this.storage.get(selector)?.has(argsKey)) {
			return this.storage.get(selector)?.get(argsKey);
		}

		const result = selector.call(this, ...(args as Array<any>));
		const storageExists = this.storage.has(selector);
		const inner = storageExists ? this.storage.get(selector)! : new Map<string, any>();
		if (!storageExists) {
			this.storage.set(selector, inner);
		}

		inner.set(argsKey, result);
		return result;
	}

	setSelectorValue<TSelector extends (...args: any) => any>(
		selector: TSelector,
		value: ReturnType<TSelector>,
		...args: Parameters<TSelector>
	): void {
		const argsKey = JSON.stringify(args);
		const storageExists = this.storage.has(selector);
		const inner = storageExists ? this.storage.get(selector)! : new Map<string, any>();
		if (!storageExists) {
			this.storage.set(selector, inner);
		}
		inner.set(argsKey, value);
	}
}
